<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useTranslation } from 'i18next-vue';

import OverlayBadge from 'primevue/overlaybadge';

import { NotificationsIcon, UiButton } from '@peanuds/ui';

import { useNotificationsStore } from './store/notifications.store';
import NotificationsDrawer from './components/NotificationsDrawer.vue';

const { t } = useTranslation();
const notificationsStore = useNotificationsStore();
const { unreadCount } = storeToRefs(notificationsStore);
const visible = ref(false);

const badgeFontSizeClass = computed(() => {
  const digitCount = unreadCount.value.toString().length;

  if (digitCount === 2) {
    return 'notifications-nav-item__badge--two-digits';
  }

  if (digitCount >= 3) {
    return 'notifications-nav-item__badge--three-digits';
  }

  return null;
});

const open = () => {
  visible.value = true;
};
</script>

<template>
  <div class="notifications-nav-item">
    <OverlayBadge
      v-if="unreadCount > 0"
      :value="unreadCount"
      :class="['notifications-nav-item__badge', badgeFontSizeClass]"
    >
      <UiButton severity="secondary" size="s" :aria-label="t('nav.notifications')" @click="open">
        <template #icon>
          <NotificationsIcon />
        </template>
      </UiButton>
    </OverlayBadge>

    <UiButton
      v-else
      severity="secondary"
      size="s"
      :aria-label="t('nav.notifications')"
      @click="open"
    >
      <template #icon>
        <NotificationsIcon />
      </template>
    </UiButton>

    <NotificationsDrawer v-model:visible="visible" />
  </div>
</template>

<style scoped lang="scss">
.notifications-nav-item {
  display: contents;
}

.notifications-nav-item__badge {
  :deep(.p-badge) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: $static-space-4;
    min-width: $static-space-4;
    padding: 0 $static-space-1;
    padding-top: 1px;
    font-size: $text-xs;
    line-height: 1;
    font-weight: $font-weight-semibold;
    border-radius: $radius-999;
  }

  &--two-digits {
    :deep(.p-badge) {
      font-size: 8px;
    }
  }

  &--three-digits {
    :deep(.p-badge) {
      font-size: 6px;
    }
  }
}
</style>
